import { ChevronRightIcon } from '@heroicons/react/outline';
import { CheckIcon } from '@heroicons/react/solid';
import { Button } from 'components/atoms/Button';
import { Modal } from 'components/atoms/Modal';
import ModalHeading from 'components/molecules/ModalHeading/ModalHeading.component';
import GivingCoinsIcon from 'components/atoms/SVG/GivingCoinsIcon.component';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import { motion } from 'motion/react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  WEBAPP_BANKING_ONBOARDING_OURPROMISES_CLICKNEXT,
  WEBAPP_BANKING_ONBOARDING_VISITEDFEESLISTSCREEN,
} from 'utils/amplitude/events/bank.amplitude';
import { fadeTransition } from 'utils/constants';
import formatMoney from 'utils/formatMoney';

const FullTransparency: FC<{ onConfirm?: () => void }> = ({ onConfirm }) => {
  const { t } = useTranslation();

  const user = useGetCurrentCustomerData().data;

  const accountableBankingFeatures = [
    {
      title: t(
        `accountable_banking.onboarding.our_promises.iban.title.${user?.country}`,
      ),
    },
    {
      title: t(
        'accountable_banking.onboarding.our_promises.total_transparency.subtitle',
      ),
    },
    {
      title: t(
        'accountable_banking.onboarding.our_promises.deposit_protection.title',
      ),
    },
    {
      title: t(
        'accountable_banking.onboarding.our_promises.instant_access.title',
      ),
    },
  ];

  return (
    <motion.div
      {...fadeTransition}
      className="flex items-center justify-center flex-col relative pb-20"
      key="5"
    >
      <ModalHeading
        title={t('accountable_banking.onboarding.full_transparency.title')}
        titleClassName="font-degular"
        subtitleClassName="w-[80%]"
      />

      <div className="flex-1 w-[80%] mx-auto my-6">
        {accountableBankingFeatures.map((feature, i) => (
          <motion.div
            initial={{ opacity: 0, translateY: 50 }}
            animate={{
              opacity: 1,
              translateY: 0,
              transition: {
                delay: i === 0 ? 0.25 : (i * 6) / 15,
              },
            }}
            className="flex items-center space-x-4 my-3"
            key={feature.title}
          >
            <div className="bg-white border-2 border-bank-600 h-5 w-5 flex-shrink-0 rounded-full text-center flex items-center justify-center">
              <CheckIcon className="w-3 h-3 text-bank-600" />
            </div>

            <div className="text-left">
              <h3 className="text-[#1B1B1B]">{feature.title}</h3>
            </div>
          </motion.div>
        ))}
        <Modal
          name="Accountable Banking Onboarding - Fees list modal"
          trigger={(modalApi) => (
            <motion.div
              initial={{ opacity: 0, translateY: 50 }}
              animate={{
                opacity: 1,
                translateY: 0,
                transition: {
                  delay:
                    accountableBankingFeatures.length === 0
                      ? 0.25
                      : (accountableBankingFeatures.length * 6) / 15,
                },
              }}
              className="my-3"
            >
              <Button
                structure="text"
                className="text-primary-600 inline-flex gap-1"
                onClick={() => modalApi.openModal()}
                tracingEvent={WEBAPP_BANKING_ONBOARDING_VISITEDFEESLISTSCREEN}
              >
                <span className="underline">
                  {t(
                    'accountable_banking.onboarding.full_transparency.fees_list_cta',
                  )}
                </span>
                <span>
                  <ChevronRightIcon className="w-4 h-4" />
                </span>
              </Button>
            </motion.div>
          )}
          className="!max-w-[40rem]"
          render={(api) => {
            return (
              <motion.div
                {...fadeTransition}
                className="flex items-center justify-center flex-col relative"
              >
                <ModalHeading Icon={GivingCoinsIcon} title="Zero hidden fees" />

                <div className="my-4 text-center border border-taxes-100 rounded-lg w-[80%]">
                  <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                    <div className="flex flex-col items-start">
                      <h3 className="text-primary-700 capitalize text-sm">
                        {t('wallet.tutorial.slide_b.fee_list.a')}
                      </h3>

                      <p className="text-primary font-semibold text-sm">€0</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                    <div className="flex flex-col items-start">
                      <h3 className="text-primary-700 capitalize text-sm">
                        {t('wallet.tutorial.slide_b.fee_list.b')}
                      </h3>

                      <p className="text-primary font-semibold text-sm">€0</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                    <div className="flex flex-col items-start">
                      <h3 className="text-primary-700 text-sm">
                        {t('wallet.tutorial.slide_b.fee_list.c')}
                      </h3>

                      <p className="text-primary font-semibold text-sm">€0</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                    <div className="flex flex-col items-start">
                      <h3 className="text-primary-700 text-sm">
                        {t('wallet.tutorial.slide_b.fee_list.d')}
                      </h3>

                      <p className="text-primary font-semibold text-sm">€0</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                    <div className="flex flex-col items-start">
                      <h3 className="text-primary-700 text-sm">
                        {t('wallet.tutorial.slide_b.fee_list.e')}
                      </h3>

                      <p className="text-primary font-semibold first-letter:capitalize text-sm">
                        €0
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                    <div className="flex flex-col items-start">
                      <h3 className="text-primary-700 text-sm">
                        {t('wallet.tutorial.slide_b.fee_list.f')}
                      </h3>

                      <p className="text-primary font-semibold first-letter:capitalize text-sm">
                        €0
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                    <div className="flex flex-col items-start">
                      <h3 className="text-primary-700 text-sm">
                        {t('wallet.tutorial.slide_b.fee_list.g')}
                      </h3>

                      <p className="text-primary font-semibold first-letter:capitalize text-sm">
                        €0
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                    <div className="flex flex-col items-start">
                      <h3 className="text-primary-700 text-sm">
                        {t('wallet.tutorial.slide_b.fee_list.h')}
                      </h3>

                      <p className="text-primary font-semibold first-letter:capitalize text-sm">
                        {t('wallet.tutorial.slide_b.fee_list.h.fee_amount', {
                          currencySymbol: '€',
                        })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                    <div className="flex flex-col items-start">
                      <h3 className="text-primary-700 text-sm">
                        {t('wallet.tutorial.slide_b.fee_list.i')}
                      </h3>

                      <p className="text-primary font-semibold first-letter:capitalize text-sm">
                        {t('wallet.tutorial.slide_b.fee_list.i.value', {
                          amount: formatMoney(5),
                        })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-5 py-3">
                    <div className="flex flex-col items-start">
                      <h3 className="text-primary-700 text-sm">
                        {t('wallet.tutorial.slide_b.fee_list.j')}
                      </h3>

                      <p className="text-primary font-semibold first-letter:capitalize text-sm">
                        {t('wallet.tutorial.slide_b.fee_list.j.value')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-5 py-3">
                    <div className="flex flex-col items-start">
                      <h3 className="text-primary-700 text-sm">
                        {t('banking_onboarding.fees.card_payments_outside_eu')}
                      </h3>

                      <p className="text-primary font-semibold first-letter:capitalize text-sm">
                        {t(
                          'banking_onboarding.fees.card_payments_outside_eu.percent',
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                <span className="text-start w-[80%] text-xs">
                  {t('banking_onboarding.fees.swan_pay')}
                </span>

                <Button
                  color="bank"
                  className="mt-6 w-[80%] "
                  onClick={() => api.closeModal()}
                >
                  {t('close')}
                </Button>
              </motion.div>
            );
          }}
        />
      </div>

      <Button
        color="bank"
        className="mt-6 w-[80%] absolute bottom-0 capitalize"
        tracingEvent={WEBAPP_BANKING_ONBOARDING_OURPROMISES_CLICKNEXT}
        onClick={onConfirm}
      >
        {t('next')}
      </Button>
    </motion.div>
  );
};

export default FullTransparency;
